import classNames from 'classnames';
import pluralize from 'pluralize';
import { useCallback, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { commonPaths } from '@/modules/common/routes.tsx';
import ArchivedItemAction from '@/modules/admin-dashboard/components/ArchivedItemAction.tsx';
import DeleteFolderModal from '@/modules/admin-dashboard/components/modals/DeleteFolderModal.tsx';
import DocumentMovingModal from '@/modules/admin-dashboard/components/modals/DocumentMovingModal.tsx';
import DocumentsFolderModal from '@/modules/admin-dashboard/components/modals/DocumentsFolderModal.tsx';
import { CURRENT_FOLDER_QK } from '@/modules/admin-dashboard/pages/Documents.tsx';
import {
  useCreatePublicFolderLinkMutation,
  useDeletePublicFolderLinkMutation,
  useRestoreFolderMutation,
} from '@/modules/admin-dashboard/queries/documents-folders.ts';
import { DropdownMenu, DropdownMenuItem, Icon, ModalProviderContext } from 'ui';
import { DotsHorizontalIcon, FolderIcon } from 'ui/icons';
import { copyTextToClipBoard } from 'ui/utils';
import { DocumentFolderTreeNode } from 'compliance-api';

interface DocumentsFolderItemProps {
  folder: DocumentFolderTreeNode;
  clientId?: number;
  hideClientSelector?: boolean;
  isPublic?: boolean;
}

const DocumentsFolderItem = ({
  folder,
  clientId,
  hideClientSelector,
  isPublic = false,
}: DocumentsFolderItemProps) => {
  const [params, setParams] = useSearchParams();
  const { openModal } = useContext(ModalProviderContext);

  const handleCopyLink = useCallback((public_folder_id?: string) => {
    if (public_folder_id) {
      const documentLink = `${window.location.origin}${commonPaths.dynamic.publicFolder(
        public_folder_id,
      )}`;
      copyTextToClipBoard(String(documentLink), {
        successMessage: 'Public folder link copied successfully',
      });
    }
  }, []);

  const {
    mutateAsync: mutateAsyncCreatePublicFolderLink,
    isLoading: isLoadingCreatePublicFolderLink,
  } = useCreatePublicFolderLinkMutation({
    onSuccess: handleCopyLink,
  });

  const {
    mutateAsync: mutateAsyncDeletePublicFolderLink,
    isLoading: isLoadingDeletePublicFolderLink,
  } = useDeletePublicFolderLinkMutation();

  const handleEditFolder = useCallback(() => {
    openModal(DocumentsFolderModal, {
      initialFolder: folder,
      clientId,
      hideClientSelector,
    });
  }, [folder, openModal, clientId, hideClientSelector]);

  const handleMoveFolder = useCallback(() => {
    openModal(DocumentMovingModal, {
      itemId: folder.id,
      isFolder: true,
      itemName: folder.name,
      clientId,
    });
  }, [folder.id, folder.name, openModal, clientId]);

  const handleDeleteFolder = useCallback(() => {
    openModal(DeleteFolderModal, { folder });
  }, [folder, openModal]);

  const handleGoToFolder = useCallback(() => {
    params.set(CURRENT_FOLDER_QK, folder.id.toString());
    setParams(params);
  }, [folder.id, params, setParams]);

  const handleCreatePublicLink = useCallback(() => {
    if (folder.public_folder_id) {
      handleCopyLink(folder.public_folder_id);
      return;
    }
    mutateAsyncCreatePublicFolderLink({
      folderId: folder.id,
    });
  }, [folder.public_folder_id, folder.id, mutateAsyncCreatePublicFolderLink, handleCopyLink]);

  const handleDeletePublicLink = useCallback(() => {
    mutateAsyncDeletePublicFolderLink({
      folderId: folder.id,
    });
  }, [folder.id, mutateAsyncDeletePublicFolderLink]);

  const items = useMemo<DropdownMenuItem[]>(() => {
    const items: DropdownMenuItem[] = [
      { label: 'Move', onClick: handleMoveFolder },
      { label: 'Update', onClick: handleEditFolder },
      { label: 'Archive', onClick: handleDeleteFolder },
      {
        label: folder?.public_folder_id ? 'Copy share link' : 'Create share link',
        onClick: handleCreatePublicLink,
        disabled: isLoadingCreatePublicFolderLink,
      },
    ];
    if (folder?.public_folder_id) {
      items.push({
        label: 'Delete share link',
        onClick: handleDeletePublicLink,
        disabled: isLoadingDeletePublicFolderLink,
      });
    }

    return items;
  }, [
    folder?.public_folder_id,
    handleCreatePublicLink,
    handleDeleteFolder,
    handleDeletePublicLink,
    handleEditFolder,
    handleMoveFolder,
    isLoadingCreatePublicFolderLink,
    isLoadingDeletePublicFolderLink,
  ]);

  return (
    <div
      className={classNames('flex justify-between p-4 border border-gray-200 rounded-xl', {
        'bg-gray-100': folder.deleted_at,
      })}
    >
      <div className="flex gap-2.5 items-center group cursor-pointer" onClick={handleGoToFolder}>
        <div className="w-[42px] h-[42px] flex justify-center items-center rounded-full bg-[rgba(229,_229,_229,_0.5)]">
          <Icon Source={FolderIcon} rawColor={folder.color} />
        </div>
        <div>
          <div className="text-sm group-hover:underline truncate max-w-40">{folder.name}</div>
          <div className="text-xs text-gray-500">
            {folder.documents_count
              ? `${folder.documents_count} ${pluralize('files', folder.documents_count)}`
              : 'No files'}
          </div>
        </div>
      </div>
      {isPublic ? (
        <></>
      ) : folder.deleted_at ? (
        <ArchivedItemAction
          id={folder.id}
          mutation={useRestoreFolderMutation}
          classes={{ label: 'hidden', container: '!w-[120px]' }}
        />
      ) : (
        <DropdownMenu
          items={items}
          button={<img alt="more" src={DotsHorizontalIcon} />}
          containerClassName="!w-auto"
          className="w-[180px]"
        />
      )}
    </div>
  );
};

export default DocumentsFolderItem;
